// libraries
import React, { useState, useEffect } from 'react'
import clsx from 'clsx'

// components
import Section from '/react4xp/components/Section'
import SectionDivider from '/react4xp/components/SectionDivider'
import RichText from '/react4xp/components/RichText'
import Image from '/react4xp/components/Image'
import Video from '/react4xp/components/Video'

// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils.js'

// svg
import { GeneralClock, GeneralMilk, GeneralNut } from '/react4xp/components/Svg/Icons/General'
import { UxAngleDown } from '/react4xp/components/Svg/Ux/Angle'

// prop types
export interface RecipePageWrapperProps {
    topPart?: {
		bgColor?: 'mini' | string
		whiteText?: boolean
		image?: {
			src?: string
			srcMedium?: string
			alt?: string
		}
		title?: string
		difficulty?: string
		time?: string
		desc?: string
	}
	ingredients?: {
		sectionTitle?: string
		portions?: string
		items?: string[]
	}
	howTo?: {
		sectionTitle?: string
		content?: string
	}
	allergens?: {
		allergensTitle?: string
		allergensItems?: {
			icon?: string
			title?: string
		}[]
		tracesOfTitle?: string
		tracesOfItems?: {
			icon?: string
			title?: string
		}[]
	}
	tips?: {
		sectionTitle?: string
		content?: string
	}
}

export default function RecipePageWrapper({
	topPart,
	ingredients,
	howTo,
	allergens,
	tips
}: RecipePageWrapperProps) {

	// custom bg color
    const customBgColor = topPart?.bgColor && !['mini'].includes(topPart?.bgColor) ? { color: topPart?.bgColor } : {}

	// toggle all details blocks
	const [isIngredientsActive, setIsIngredientsActive] = useState(false)
	const toggleIngredients = () => { setIsIngredientsActive((prev) => !prev) }

	const [isStepsActive, setIsStepsActive] = useState(false)
	const toggleSteps = () => { setIsStepsActive((prev) => !prev) }

	const [isAllergensActive, setIsAllergensActive] = useState(false)
	const toggleAllergens = () => { setIsAllergensActive((prev) => !prev) }

	const [isTipsActive, setIsTipsActive] = useState(false)
	const toggleTips = () => { setIsTipsActive((prev) => !prev) }

	// change the accordion to open / closed based on the window width
	const [lastWidth, setLastWidth] = useState(null)

	useEffect(() => {
        const checkWindowWidth = () => {
            const currentWidth = window.innerWidth

            if (currentWidth !== lastWidth) {
                setLastWidth(currentWidth)
                const isWideScreen = currentWidth >= 768

				setIsIngredientsActive(isWideScreen)
                setIsStepsActive(isWideScreen)
                setIsAllergensActive(isWideScreen)
				setIsTipsActive(isWideScreen)
            }
        }

        checkWindowWidth()

        window.addEventListener('resize', checkWindowWidth)

        return () => {
            window.removeEventListener('resize', checkWindowWidth)
        }
    }, [lastWidth])

	// update tabindex based on the window width
	const [tabIndex, setTabIndex] = useState(null)

	useEffect(() => {
		const updateTabIndex = () => {
			if (window.innerWidth >= 768) {
				setTabIndex(-1)
			} else {
				setTabIndex(null)
			}
		}

		updateTabIndex()

		window.addEventListener('resize', updateTabIndex)

		return () => window.removeEventListener('resize', updateTabIndex)
	}, [])

	return (
		<div className='page-recipe'>

			<Section
				bgColor={topPart?.bgColor}
				whiteText={topPart?.whiteText}
				className={clsx(
					'page-recipe__header',
					topPart?.bgColor && 'page-recipe__header--has-bg-color'
				)}
				marginTop='none'
				marginBottom='none'
			>
				<div className='container'>
					<div className='row'>

						{topPart?.image?.src && (
							<div className={clsx('col-md-6 col-md-push-6')}>
								<div className='page-recipe__header__image-wrapper'>
									<Image
										src={topPart?.image?.src} // 700px x 700px
										srcMedium={topPart?.image?.srcMedium} // 550px x 500px
										width={700}
										alt={topPart?.image?.alt}
										sizes='
											(max-width: 768px) 100vw,
											50vw
										'
										className='cover'
									/>
								</div>
							</div>
						)}

						<div className={clsx(
                            'page-recipe__header__texts-wrapper',
                            topPart?.image?.src ? 'col-md-6 col-md-pull-6' : 'col-12 page-recipe__header__texts-wrapper--no-image'
                        )}>

                            {isNonWhitespaceString(topPart?.title) && (
                                <h1 className='h1-hero'>
                                    {topPart?.title}
                                </h1>
                            )}

							<div className='tags-block'>

								{isNonWhitespaceString(topPart?.difficulty) && (
									<div className='difficulty text-smaller'>
										{topPart?.difficulty}
									</div>
								)}

								{isNonWhitespaceString(topPart?.time) && (
									<div className='time'>
										
										<GeneralClock variant='outline' />

										<p className='text-small'>
											{topPart?.time}
										</p>

									</div>
								)}

							</div>

                            {isNonWhitespaceString(topPart?.desc) && (
                                <div
									className='h3'
									dangerouslySetInnerHTML={{ __html: topPart?.desc }}
									suppressHydrationWarning
								/>
                            )}

                        </div>

					</div>
				</div>
			</Section>

			{topPart?.bgColor && (
                <svg
                    width='1440'
                    height='14'
                    viewBox='0 0 1440 14'
                    className={clsx(
                        'page-recipe__header-divider',
                        topPart?.bgColor === 'mini' && 'mini'
                    )}
                    xmlns='http://www.w3.org/2000/svg'
                    style={customBgColor}
                >
                    <path d='M1246.57 13.5451C1255.29 13.3297 1440 2.54519 1440 2.54519V0L0 0.000125933L2.22433e-07 2.54532L311.392 13.5452C311.392 13.5452 638.12 1.42504 846.432 2.8297C1003.42 3.88829 1237.84 13.7606 1246.57 13.5451Z' fill='currentColor'/>
                </svg>            
            )}

			{!topPart?.bgColor && (
				<SectionDivider className='page-recipe__divider' />
			)}

			<Section
				className='page-recipe__details'
				marginTop='small'
				marginBottom='small'
			>
				<div className='container'>
					<div className='row'>

						{(ingredients?.items?.length > 0) && (
							<div className='col-md-5 left'>
								<div className='left-inner'>
									<div className={clsx(
										'accordion',
										isIngredientsActive && 'is-active'
									)}>

										<button
											type='button'
											className='accordion__title'
											onClick={toggleIngredients}
											tabIndex={tabIndex}
										>

											<h3 className='products-box__title h3'>
												{ingredients?.sectionTitle}
											</h3>

											<UxAngleDown />

										</button>

										<div className='accordion__content'>
											<div>
												{
													ingredients?.portions &&
													<p>
														{ingredients?.portions}
													</p>
												}
												<div className='tags-block h5'>

													{ingredients?.items?.map((item, i) => (
														<p key={i}>
															{item}
														</p>
													))}

												</div>
											</div>
										</div>

									</div>
								</div>
							</div>
						)}

						<div className={clsx(
							'right',
							(ingredients?.items?.length > 0) ? 'col-md-7' : 'col-12'
						)}>

							{howTo && (
								<div className={clsx(
									'accordion',
									isStepsActive && 'is-active'
								)}>

									<button
										type='button'
										className='accordion__title'
										onClick={toggleSteps}
										tabIndex={tabIndex}
									>

										<h3 className='products-box__title h3'>
											{howTo?.sectionTitle}
										</h3>

										<UxAngleDown />

									</button>

									<div className='accordion__content'>
										<div>
											<RichText content={howTo?.content} />
										</div>
									</div>

								</div>
							)}

							{((allergens?.allergensItems?.length > 0) || (allergens?.tracesOfItems?.length > 0)) && (
								<div className={clsx(
									'accordion',
									isAllergensActive && 'is-active'
								)}>

									<button
										type='button'
										className='accordion__title'
										onClick={toggleAllergens}
										tabIndex={tabIndex}
									>

										<h3 className='products-box__title h3'>
											{allergens?.allergensTitle}
										</h3>

										<UxAngleDown />

									</button>

									<div className='accordion__content'>
										<div>

											{(allergens?.allergensItems?.length > 0) &&
												<div className='tags-block text-small'>
													{allergens?.allergensItems?.map((item, i) =>
														<div
															className='shape-mask'
															key={i}
														>
															{item.icon && (
																<span
																	dangerouslySetInnerHTML={{ __html: item.icon }}
																	suppressHydrationWarning
																/>
															)}
															<p>{item.title}</p>
														</div>
													)}
												</div>
											}

											{(allergens?.tracesOfItems?.length > 0) &&
												<>
													<div className='accordion__title'>
														<h3 className='products-box__title h4'>
															{allergens?.tracesOfTitle}
														</h3>
													</div>

													<div className='tags-block text-small'>
														{allergens?.tracesOfItems?.map((item, i) =>
															<div
																className='shape-mask'
																key={i}
															>
																{item.icon && (
																	<span
																		dangerouslySetInnerHTML={{ __html: item.icon }}
																		suppressHydrationWarning
																	/>
																)}
																<p>{item.title}</p>
															</div>
														)}
													</div>
												</>
											}
										</div>
									</div>

								</div>
							)}

							{(tips?.content?.length > 0) && (
								<div className={clsx(
									'accordion',
									isTipsActive && 'is-active'
								)}>

									<button
										type='button'
										className='accordion__title'
										onClick={toggleTips}
										tabIndex={tabIndex}
									>

										<h3 className='products-box__title h3'>
											{tips?.sectionTitle}
										</h3>

										<UxAngleDown />

									</button>

									<div className='accordion__content'>
										<div>
											<RichText content={tips?.content} />
										</div>
									</div>

								</div>
							)}

						</div>

					</div>
				</div>
			</Section>

		</div>
	)
}
